interface CachedSearchResult {
  query: string;
  results: any[];
  timestamp: number;
}

interface SearchCacheStorage {
  [key: string]: CachedSearchResult;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEY = 'book-search-cache';

export class SearchCache {
  private static getStorageKey(query: string): string {
    return `${CACHE_KEY}-${query.toLowerCase().trim()}`;
  }

  static set(query: string, results: any[]): void {
    try {
      const storageKey = this.getStorageKey(query);
      const cachedResult: CachedSearchResult = {
        query: query.toLowerCase().trim(),
        results,
        timestamp: Date.now()
      };
      
      localStorage.setItem(storageKey, JSON.stringify(cachedResult));
    } catch (error) {
      console.warn('Failed to cache search results:', error);
    }
  }

  static get(query: string): any[] | null {
    try {
      const storageKey = this.getStorageKey(query);
      const cached = localStorage.getItem(storageKey);
      
      if (!cached) return null;
      
      const cachedResult: CachedSearchResult = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid (less than 24 hours old)
      if (now - cachedResult.timestamp > CACHE_DURATION) {
        localStorage.removeItem(storageKey);
        return null;
      }
      
      return cachedResult.results;
    } catch (error) {
      console.warn('Failed to retrieve cached search results:', error);
      return null;
    }
  }

  static clear(): void {
    try {
      // Remove all search cache entries
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear search cache:', error);
    }
  }

  static clearExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const cachedResult: CachedSearchResult = JSON.parse(cached);
              if (now - cachedResult.timestamp > CACHE_DURATION) {
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // Remove invalid cache entries
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to clear expired search cache:', error);
    }
  }
}